import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Info } from "lucide-react";
import { oxpayAPI } from "@/lib/oxpay";

export default function UserInfoDialog({ oxpayUserId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [oxpayUserData, setOxpayUserData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOxPayData = async () => {
    if (!oxpayUserId) {
      setOxpayUserData(null);
      return;
    }

    setLoading(true);
    try {
      // Call OxPay API with user ID
      const oxpayResponse = await oxpayAPI.getUser(oxpayUserId);
      console.log("OxPay user data:", oxpayResponse);

      if (oxpayResponse?.body) {
        setOxpayUserData(oxpayResponse.body);
      }
    } catch (error) {
      console.error("Error fetching OxPay user data:", error);
      setOxpayUserData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && oxpayUserId) {
      fetchOxPayData();
    }
  }, [open, oxpayUserId]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {/* Info Icon Button */}
      <button
        onClick={() =>
          window.location.hostname === "localhost"
            ? setOpen(true)
            : navigate("/profile")
        }
        className="ml-2 p-1 rounded-full hover:bg-muted transition-colors cursor-pointer"
        title="View User Info"
      >
        <Info className="h-5 w-5 text-muted-foreground" />
      </button>

      {/* User Info Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Information</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
            </div>
          ) : oxpayUserData ? (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Basic Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Email:</span>
                    <p className="font-medium">
                      {oxpayUserData?.email || "N/A"}
                    </p>
                  </div>
                  {oxpayUserData?.customer_id && (
                    <div>
                      <span className="text-muted-foreground">
                        Customer ID:
                      </span>
                      <p className="font-medium">{oxpayUserData.customer_id}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Personal Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Title:</span>
                    <p className="font-medium">{oxpayUserData?.title || ""}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Gender:</span>
                    <p className="font-medium capitalize">
                      {oxpayUserData?.gender || ""}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">First Name:</span>
                    <p className="font-medium">
                      {oxpayUserData?.name?.first || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last Name:</span>
                    <p className="font-medium">
                      {oxpayUserData?.name?.last || "N/A"}
                    </p>
                  </div>
                  {oxpayUserData?.name?.middle && (
                    <div>
                      <span className="text-muted-foreground">
                        Middle Name:
                      </span>
                      <p className="font-medium">
                        {oxpayUserData?.name?.middle}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Phone:</span>
                    <p className="font-medium">
                      {oxpayUserData?.mobile
                        ? `+${oxpayUserData.mobile.country_code} ${oxpayUserData.mobile.number}`
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Nationality:</span>
                    <p className="font-medium">
                      {oxpayUserData?.nationality || "N/A"}
                    </p>
                  </div>
                  {oxpayUserData?.birthday && (
                    <div>
                      <span className="text-muted-foreground">
                        Date of Birth:
                      </span>
                      <p className="font-medium">
                        {formatDate(oxpayUserData?.birthday)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Identification Info */}
              {oxpayUserData?.identification && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm">Identification</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {oxpayUserData.identification.type && (
                      <div>
                        <span className="text-muted-foreground">ID Type:</span>
                        <p className="font-medium capitalize">
                          {oxpayUserData.identification.type}
                        </p>
                      </div>
                    )}
                    {oxpayUserData.identification.number && (
                      <div>
                        <span className="text-muted-foreground">
                          ID Number:
                        </span>
                        <p className="font-medium">
                          {oxpayUserData.identification.number}
                        </p>
                      </div>
                    )}
                    {oxpayUserData.country_of_issue && (
                      <div>
                        <span className="text-muted-foreground">
                          Issuing Country:
                        </span>
                        <p className="font-medium">
                          {oxpayUserData.country_of_issue}
                        </p>
                      </div>
                    )}
                    {oxpayUserData.identification.expiry && (
                      <div>
                        <span className="text-muted-foreground">
                          Expiry Date:
                        </span>
                        <p className="font-medium">
                          {formatDate(oxpayUserData.identification.expiry)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Address Info - from OxPay if available */}

              {/* Account Status */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm">Account Status</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">OxPay Status:</span>
                    <p
                      className={`font-medium ${
                        oxpayUserData?.status?.state === "active"
                          ? "text-green-600"
                          : oxpayUserData?.status?.state === "suspended"
                          ? "text-yellow-600"
                          : "text-red-600"
                      }`}
                    >
                      {oxpayUserData?.status?.text || "Not Created"}
                    </p>
                  </div>
                  {oxpayUserData?.status?.is_active !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Active:</span>
                      <p
                        className={`font-medium ${
                          oxpayUserData.status.is_active
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {oxpayUserData.status.is_active ? "Yes" : "No"}
                      </p>
                    </div>
                  )}
                  {/* <div>
                    <span className="text-muted-foreground">KYC Status:</span>
                    <p
                      className={`font-medium ${
                        oxpayUserData?.kyc?.status === "approved"
                          ? "text-green-600"
                          : oxpayUserData?.kyc?.status === "pending"
                          ? "text-yellow-600"
                          : oxpayUserData?.kyc?.status === "rejected"
                          ? "text-red-600"
                          : "text-gray-600"
                      }`}
                    >
                      {oxpayUserData?.kyc?.status || "Not Started"}
                    </p>
                  </div> */}
                  {/* <div>
                    <span className="text-muted-foreground">KYC Level:</span>
                    <p className="font-medium">
                      Level {oxpayUserData?.kyc?.level || 0}
                    </p>
                  </div> */}
                  {oxpayUserData?.risk_assessment_status && (
                    <div>
                      <span className="text-muted-foreground">
                        Risk Assessment:
                      </span>
                      <p
                        className={`font-medium ${
                          oxpayUserData.risk_assessment_status === "CLEARED"
                            ? "text-green-600"
                            : oxpayUserData.risk_assessment_status === "PENDING"
                            ? "text-yellow-600"
                            : oxpayUserData.risk_assessment_status ===
                              "REJECTED"
                            ? "text-red-600"
                            : ""
                        }`}
                      >
                        {oxpayUserData.risk_assessment_status}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium">
                      {formatDate(oxpayUserData?.date?.registration)}
                    </p>
                  </div>
                </div>
              </div>

              {/* OxPay IDs */}
              {oxpayUserData?.id && (
                <div className="space-y-2">
                  {/* <h3 className="font-semibold text-sm">System IDs</h3> */}
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-muted-foreground">
                        Acccount ID:
                      </span>
                      <p className="font-mono text-xs break-all">
                        {oxpayUserData.id}
                      </p>
                    </div>
                    {oxpayUserData?.partner_id && (
                      <div>
                        <span className="text-muted-foreground">
                          Partner ID:
                        </span>
                        <p className="font-mono text-xs break-all">
                          {oxpayUserData.partner_id}
                        </p>
                      </div>
                    )}
                    {oxpayUserData?.agent_id && (
                      <div>
                        <span className="text-muted-foreground">Agent ID:</span>
                        <p className="font-mono text-xs break-all">
                          {oxpayUserData.agent_id}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {!oxpayUserId
                  ? "User not registered"
                  : "No user data available"}
              </p>
              {!oxpayUserId && (
                <p className="text-xs text-muted-foreground mt-2">
                  Please complete user registration first
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
